import { motion } from 'motion/react';
import { useState } from 'react';
import { ErrorBanner } from '../../components/PageShell';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/cn';
import { useGuardedAction } from '../../lib/hooks';
import { unlock } from './access';

/** Asks for the server's access code, which starting a game needs. Joining a party never does. */
export function AccessCard({ className }: { className?: string }) {
  const [code, setCode] = useState('');
  const { run, busy, error } = useGuardedAction(() => unlock(code), 'Could not check the code.');

  return (
    <motion.div
      className={cn('glass overflow-hidden rounded-3xl p-5 ring-1 ring-highlight-400/30 sm:p-6', className)}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0, marginTop: 0 }}
    >
      <div className="flex items-start gap-4">
        <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-ink-800 text-2xl ring-1 ring-white/8">🔒</span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-extrabold">Private server</h2>
          <p className="mt-1 text-sm text-ink-300">
            Starting a game needs the access code. Invited to a party? Join it with its code or link, no access code
            needed.
          </p>
          <form
            className="mt-4 flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (code.trim()) void run();
            }}
          >
            <input
              type="password"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Access code"
              aria-label="Access code"
              autoComplete="current-password"
              className="h-11 w-full min-w-0 rounded-2xl bg-ink-850 px-4 ring-1 ring-white/10 outline-none placeholder:text-ink-600 focus:ring-2 focus:ring-highlight-400"
            />
            <Button type="submit" variant="secondary" loading={busy} disabled={!code.trim()}>
              Unlock
            </Button>
          </form>
          <ErrorBanner message={error} className="mt-3" />
        </div>
      </div>
    </motion.div>
  );
}
